/**
 * Derives a light background / dark text pair from a label's own hex color,
 * keeping the same hue (e.g. bg-[#FFE4E6] text-[#E11D48] style pills).
 */

const hexToRgb = hex => {
  let value = hex.replace('#', '');
  if (value.length === 3) {
    value = value
      .split('')
      .map(c => c + c)
      .join('');
  }
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
};

const rgbToHsl = ({ r, g, b }) => {
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rN:
        h = (gN - bN) / d + (gN < bN ? 6 : 0);
        break;
      case gN:
        h = (bN - rN) / d + 2;
        break;
      default:
        h = (rN - gN) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h, s, l };
};

const hue2rgb = (p, q, tIn) => {
  let t = tIn;
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
};

const hslToHex = (h, s, l) => {
  let r;
  let g;
  let b;
  if (s === 0) {
    r = l;
    g = l;
    b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = x =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * @param {string} hex Label color, e.g. '#1f93ff'
 * @returns {{ bg: string, text: string }} Light-tint background + dark-shade text, same hue
 */
export const getLabelPillColors = hex => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return { bg: hex, text: '' };
  }
  const { h, s } = rgbToHsl(hexToRgb(hex));
  // Guarantee a visible tint even for near-grey label colors.
  const saturation = Math.max(s, 0.45);
  return {
    bg: hslToHex(h, Math.min(saturation, 0.9), 0.92),
    text: hslToHex(h, Math.min(saturation + 0.1, 1), 0.32),
  };
};
